import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Stack, IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { mkConfig, generateCsv, download } from "export-to-csv";

import TopBar from "../../../../../component/TopBar";
import MainBox from "../../../container/MainBox";
import DataTable from "../../../../../component/DataTable";
import Counter from "../../../../../component/Counter";
import InlineMessage from "../../../../../component/InlineMessage";
import BtnPrimary from "../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../component/SecondaryButton";
import { StatusChip } from "../../../../../component/listing";
import MonthStrip, { MonthStripLegend, toneColors } from "../../../../../component/attendance/MonthStrip";
import PunchCorrection from "../../../../../component/attendance/PunchCorrection";
import RequestUpdatesTable from "./RequestUpdatesTable";
import ShiftChangeForm from "./ShiftChangeForm";

import { getAllDaySelfAttendance } from "../../../../../services/facialRecognition";
import { getSelfShift } from "../../../../../services/shift";
import { getAllHolidays } from "../../../../../services/holiday";
import { getLeaveCalendar } from "../../../../../services/leave";
import { createAttendanceRequest } from "../../../../../services/attendanceRequest";
import { useNotify } from "../../../../../component/NotificationProvider";

import { buildAttendanceMonth, toDayString } from "../../../../../utils/attendanceMonth";
import { calculateTime } from "../../../../../utils/calculateTime";
import { counterRowSx } from "../../../container/frameLayout";
import { tokens, displayFont } from "../../../../../theme";
import {
  monthName, presenceLine, shiftLine, hoursSummary, missingPunchNote, dayAction,
  attendedCount, workingDayCount,
} from "./selfAttendance";

/**
 * The employee's own attendance register.
 *
 * This screen used to be a different product: a punctuality percentage, a
 * leaderboard of who clocked in earliest, and two tables that were wired to
 * nothing — `TableView` rendered a single empty row and `LeaderBoard` had no
 * data source at all. None of it answered the question somebody actually opens
 * this page with, which is "is my month right, and if not, what do I do about
 * it".
 *
 * So it is now the same register the admin sees on an employee's profile, from
 * the other side: the whole month as coded cells, the counts underneath, and an
 * action on exactly the two kinds of day the employee is the only person who
 * can resolve. The month is built by `buildAttendanceMonth` — the same function
 * the admin register and the muster use — so the three can never disagree about
 * what a given day was.
 */

const Dash = () => (
  <Box component="span" aria-label="none" sx={{ color: tokens.textPlaceholder }}>
    —
  </Box>
);

const EmployeeAttendanceDashboard = () => {
  const notify = useNotify();
  const today = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState({ year: today.getFullYear(), month: today.getMonth() });

  const [records, setRecords] = useState([]);
  const [shift, setShift] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [leave, setLeave] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  // which cell the correction popover hangs off, and the day it is editing
  const [editing, setEditing] = useState({ anchorEl: null, dayString: null });
  const [shiftChange, setShiftChange] = useState(false);
  // bumped so the requests list picks up one the employee just filed
  const [requestsKey, setRequestsKey] = useState(0);

  const { year, month } = cursor;
  const isCurrentMonth = year === today.getFullYear() && month === today.getMonth();
  const todayString = toDayString(today.getFullYear(), today.getMonth(), today.getDate());

  /*
   * One fetch for the whole history, not one per month.
   *
   * `/days/self` takes no date range — unlike the admin's per-employee endpoint,
   * which does — so stepping backwards through months is filtered here rather
   * than refetched. That is the right trade for one person's own record: a few
   * hundred rows at most, and stepping months costs nothing.
   */
  const fetchAll = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setFailed(false);
    const [attendance, shiftRes, holidayRes, leaveRes] = await Promise.all([
      getAllDaySelfAttendance(),
      getSelfShift().catch(() => null),
      getAllHolidays().catch(() => null),
      // Scoped to the caller server-side. Until the calendar route stops being
      // admin-only this returns 403 for an employee, and the register renders
      // without leave days rather than not at all — which is why it is caught
      // here rather than allowed to fail the whole screen.
      getLeaveCalendar({ status: "Approved" }).catch(() => null),
    ]);
    // Only attendance failing is worth an error state. No shift, no holidays and
    // no leave are all legitimate — the month reads correctly as absent data,
    // and `buildAttendanceMonth` already declines to call a day "absent" when it
    // has no roster to judge it against.
    if (attendance?.success === false) setFailed(true);
    setRecords(Array.isArray(attendance?.data) ? attendance.data : []);
    setShift(shiftRes?.data || null);
    setHolidays(Array.isArray(holidayRes?.data) ? holidayRes.data : []);
    setLeave(Array.isArray(leaveRes?.data) ? leaveRes.data : []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // a new month invalidates any day selected in the previous one
  useEffect(() => { setSelectedDay(null); }, [year, month]);

  const { days } = useMemo(
    () => buildAttendanceMonth({ year, month, records, shift, holidays, leave, today }),
    [year, month, records, shift, holidays, leave, today]
  );

  const hours = useMemo(() => hoursSummary(days), [days]);
  const note = useMemo(() => missingPunchNote(days), [days]);
  const absent = useMemo(() => days.filter((d) => d.status.key === "ABSENT"), [days]);

  const editingDay = useMemo(
    () => days.find((d) => d.dayString === editing.dayString) || null,
    [days, editing.dayString]
  );

  // the strip covers the whole month; the table only lists days that have resolved
  const tableDays = useMemo(
    () => days.filter((d) => d.status.key !== "UPCOMING"),
    [days]
  );

  const rowRefs = useRef({});
  useEffect(() => {
    if (!selectedDay) return;
    rowRefs.current[selectedDay]?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [selectedDay, tableDays]);

  const step = (delta) =>
    setCursor((c) => {
      const next = new Date(Date.UTC(c.year, c.month + delta, 1));
      return { year: next.getUTCFullYear(), month: next.getUTCMonth() };
    });

  const openEditor = (event, dayString) => {
    event.stopPropagation();
    setEditing({ anchorEl: event.currentTarget, dayString });
  };
  const closeEditor = () => setEditing({ anchorEl: null, dayString: null });

  const submitCorrection = async (payload) => {
    // No employeeId and no source: this is the employee's own request, which is
    // what an unattested regularization is. Naming themselves as the attester
    // is the audit hole the admin register's split exists to close.
    const res = await createAttendanceRequest({
      type: "REGULARIZATION",
      dayString: editing.dayString,
      ...payload,
    });
    if (res?.success) {
      closeEditor();
      notify.success("Sent to your manager for approval.");
      // quiet: the popover reseeds from the day it points at, so leaving the
      // screen to a loader mid-save loses the reader's place in the register
      await fetchAll({ quiet: true });
    }
    return res;
  };

  const firstFixable = useMemo(() => days.find((d) => dayAction(d) === "Fix"), [days]);

  /*
   * The month as it is read, not the records behind it.
   *
   * Exporting the raw day records would drop every day that has no record —
   * which is exactly the days somebody exports a register to argue about. So
   * this writes the resolved month: a row per calendar day, carrying the status
   * the strip and the table are showing.
   */
  const exportCsv = () => {
    const rows = tableDays.map((d) => ({
      Date: d.dayString,
      Day: d.weekday,
      Status: d.status.label,
      Note: d.remark || "",
      In: d.arrivalTime != null ? calculateTime(d.arrivalTime) : "",
      Out: d.departureTime != null ? calculateTime(d.departureTime) : "",
      Hours: d.totalHours ? (d.totalHours / 60).toFixed(1) : "",
    }));
    if (!rows.length) {
      notify.error("Nothing to export in this month");
      return;
    }
    const cfg = mkConfig({
      useKeysAsHeaders: true,
      filename: `attendance-${year}-${String(month + 1).padStart(2, "0")}`,
    });
    download(cfg)(generateCsv(cfg)(rows));
  };

  const meta = [presenceLine(days), shiftLine(shift)].filter(Boolean).join(" · ");

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar
        MainHeading={`Attendance · ${monthName(month)} ${year}`}
        breadcrumbs={[{ label: "Attendance" }, { label: "My attendance" }]}
        meta={loading ? null : meta}
        actions={
          <Stack direction="row" spacing={1.25}>
            <BtnSecondary sx={{ borderRadius: "8px" }} onClick={exportCsv} disabled={loading}>
              Download
            </BtnSecondary>
            {/* The primary action is offered only when there is something to
                fix. A permanently-lit "Fix a missing punch" on a clean month
                invites somebody to file a correction against a correct day. */}
            {firstFixable && (
              <BtnPrimary
                sx={{ borderRadius: "8px" }}
                onClick={(e) => {
                  setSelectedDay(firstFixable.dayString);
                  openEditor(e, firstFixable.dayString);
                }}
              >
                Fix a missing punch
              </BtnPrimary>
            )}
          </Stack>
        }
      />

      <Box sx={{ ...counterRowSx(4), mb: "1.25rem" }}>
        <Counter
          label="Present"
          value={loading ? "—" : String(attendedCount(days))}
          sub={loading ? null : `of ${workingDayCount(days)} working days`}
        />
        <Counter label="Hours worked" value={loading ? "—" : hours.label} sub={loading ? null : hours.sub} />
        <Counter
          label="Missing punch"
          value={loading ? "—" : String(note?.count || 0)}
          sub={note ? "needs your attention" : "nothing outstanding"}
          tone={note ? tokens.warningText : undefined}
        />
        <Counter
          label="Absent"
          value={loading ? "—" : String(absent.length)}
          sub={absent.length ? absent.map((d) => `${d.dayNum} ${monthName(month).slice(0, 3)}`).join(", ") : "none this month"}
          tone={absent.length ? tokens.danger : undefined}
        />
      </Box>

      {note && (
        <InlineMessage tone="warning" title={note.lead} sx={{ mb: "1.25rem" }}>
          {note.body}
        </InlineMessage>
      )}

      {failed && (
        <InlineMessage tone="danger" title="Couldn't load your attendance." sx={{ mb: "1.25rem" }}>
          The register below may be incomplete.{" "}
          <Box
            component="button"
            type="button"
            onClick={() => fetchAll()}
            sx={{ font: "inherit", color: "inherit", background: "none", border: 0, p: 0, textDecoration: "underline", cursor: "pointer" }}
          >
            Try again
          </Box>
          .
        </InlineMessage>
      )}

      <MainBox sx={{ padding: "1.5rem", mb: "1.25rem" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: "1rem" }}>
          <Box sx={{ fontFamily: displayFont, fontSize: "1rem", fontWeight: 600, color: tokens.text }}>
            {monthName(month)} {year}
          </Box>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <IconButton size="small" onClick={() => step(-1)} aria-label="Previous month">
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            {/* Nothing to show past today: a month of empty cells reads as a
                register that lost its data rather than one that hasn't happened. */}
            <IconButton
              size="small"
              onClick={() => step(1)}
              disabled={isCurrentMonth}
              aria-label="Next month"
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        <MonthStrip
          days={days}
          selectedDay={selectedDay}
          onSelectDay={setSelectedDay}
          todayString={todayString}
        />
        <MonthStripLegend days={days} />
      </MainBox>

      <DataTable
        rows={tableDays}
        loading={loading}
        getRowId={(d) => d.dayString}
        empty={{ title: "Nothing recorded yet", description: "Days appear here as they are punched." }}
        tableSx={{ width: "100%", tableLayout: "fixed" }}
        rowSx={(d) => (d.dayString === selectedDay ? { background: tokens.surfaceSubtle } : undefined)}
        onRowClick={(d) => setSelectedDay((s) => (s === d.dayString ? null : d.dayString))}
        columns={[
          {
            key: "date",
            label: "Date",
            width: "8.5rem",
            render: (d) => (
              <Box ref={(el) => { rowRefs.current[d.dayString] = el; }}>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {String(d.dayNum).padStart(2, "0")} {monthName(month).slice(0, 3)}
                </Box>{" "}
                <Box component="span" sx={{ color: tokens.textMuted }}>{d.weekday}</Box>
              </Box>
            ),
          },
          {
            key: "status",
            label: "Status",
            width: "9.5rem",
            render: (d) => {
              // Explicit pair rather than a tone name: the chip's palette has no
              // violet, so a missing punch would quietly fall back to muted and
              // stop matching its own cell in the strip directly above.
              const { fg, bg } = toneColors(d.status.tone);
              return <StatusChip label={d.remark || d.status.label} color={fg} bg={bg} />;
            },
          },
          {
            key: "in",
            label: "In",
            align: "right",
            width: "5.5rem",
            render: (d) => (d.arrivalTime != null ? calculateTime(d.arrivalTime) : <Dash />),
          },
          {
            key: "out",
            label: "Out",
            align: "right",
            width: "5.5rem",
            render: (d) => (d.departureTime != null ? calculateTime(d.departureTime) : <Dash />),
          },
          {
            key: "hours",
            label: "Hours",
            align: "right",
            width: "5.5rem",
            render: (d) => (d.totalHours ? (d.totalHours / 60).toFixed(1) : <Dash />),
          },
          {
            key: "action",
            label: "",
            render: (d) => {
              const action = dayAction(d);
              if (!action) return <Dash />;
              return (
                <Box
                  component="button"
                  type="button"
                  onClick={(e) => openEditor(e, d.dayString)}
                  aria-label={`${action} ${d.dayNum} ${monthName(month)}`}
                  sx={{
                    font: "inherit",
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    color: tokens.primary,
                    background: "transparent",
                    border: 0,
                    p: 0,
                    cursor: "pointer",
                    "&:focus-visible": { outline: `2px solid ${tokens.accent}`, outlineOffset: "2px" },
                  }}
                >
                  {action}
                </Box>
              );
            },
          },
        ]}
      />

      <MainBox sx={{ padding: "1.5rem", mt: "1.25rem" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: "1rem" }}>
          <Box sx={{ fontFamily: displayFont, fontSize: "1rem", fontWeight: 600, color: tokens.text }}>
            Your requests
          </Box>
          {/* A shift change is a standing request about your roster, so it is
              raised from the list its answer will appear in — not, as it was,
              from a side panel that only opened once you had clicked a date the
              request has nothing to do with. */}
          <BtnSecondary sx={{ borderRadius: "8px" }} onClick={() => setShiftChange(true)}>
            Request a shift change
          </BtnSecondary>
        </Stack>
        <RequestUpdatesTable key={requestsKey} />
      </MainBox>

      <ShiftChangeForm
        open={shiftChange}
        onClose={() => setShiftChange(false)}
        onSubmitted={() => setRequestsKey((k) => k + 1)}
      />

      <PunchCorrection
        anchorEl={editing.anchorEl}
        day={editingDay}
        dateLabel={editingDay ? `${editingDay.dayNum} ${monthName(month)} ${year}` : ""}
        onClose={closeEditor}
        onSubmit={submitCorrection}
        isOwnRegister
      />
    </Box>
  );
};

export default EmployeeAttendanceDashboard;
